import List from 'antd/lib/list';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { selectNotifications } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import NotificationSettingsModal from './NotificationSettingsModal';
import Search from 'antd/lib/input/Search';
import NotificationRow from '../common/NotificationRow';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Link, useLocation } from 'react-router-dom';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';

interface AppNotificationsProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

const AppNotifications: React.FC<AppNotificationsProps> = (props: AppNotificationsProps) => {
    const { appId } = props;

    const notifications = useSelector((state: RootState) => selectNotifications(state, appId));

    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [filteredNotifications, setFilteredNotifications] = React.useState(notifications);

    const location = useLocation();
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idFromQuery = params.get('notificationId');
        if (idFromQuery) {
            setNotificationIdSelectedForSettings(idFromQuery);
            setShowNotificationSettings(true);
        }
    }, [location.search]);

    React.useEffect(() => {
        if (searchValue === '') {
            setFilteredNotifications(notifications);
        } else {
            const filteredNotifs = notifications.filter((notif) =>
                notif.displayText.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
            );

            setFilteredNotifications(filteredNotifs);
        }
    }, [searchValue, notifications]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} ref={props.refProp}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Text style={styles.appSettingsSectionTitleStyle}>Notifications</Text>
                </Space>
                <Search
                    placeholder="Search notifications"
                    allowClear
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onSearch={(value) => {
                        setSearchValue(value);
                    }}
                    style={{ width: 400 }}
                />
            </div>
            <List
                header={null}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10, size: 'small', showSizeChanger: false }}
                dataSource={filteredNotifications}
                renderItem={(notification) => {
                    return (
                        <List.Item key={notification.id}>
                            <NotificationRow
                                notification={notification}
                                notificationsEnabled
                                onClickSettings={(notificationId) => {
                                    setNotificationIdSelectedForSettings(notificationId);
                                    setShowNotificationSettings(true);
                                }}
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                            />
                        </List.Item>
                    );
                }}
            ></List>
            <Text>
                You can enable email notifications for any app-level notification from{' '}
                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                    User settings &gt; Email notifications
                </Link>
                .
            </Text>
            {notificationIdSelectedForSettings && (
                <NotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowNotificationSettings(false)}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                    }}
                />
            )}
        </div>
    );
};

export default AppNotifications;
